import {computed, Signal, signal, WritableSignal} from "@angular/core"
import {deepCopy} from "@cm/utils"
import {Operator, OperatorPanelComponentType, OperatorProcessingHints} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {OperatorBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-base"
import {OperatorCallback} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-callback"
import {HighpassPanelComponent} from "@app/textures/texture-editor/operator-stack/operators/highpass/panel/highpass-panel.component"
import {HighpassToolbox} from "@app/textures/texture-editor/operator-stack/operators/highpass/toolbox/highpass-toolbox"
import * as TextureEditNodes from "@app/textures/texture-editor/texture-edit-nodes"
import {
    AnyImageOpResult,
    AnyImageOpResultNode,
    ValueOrNode,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {prepareAndBakeMinimalMask} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/mask-brush-bake/prepare-and-bake-minimal-mask"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {z} from "zod"
import {DisplayMode} from "@app/textures/texture-editor/operator-stack/operators/highpass/node/definition"
import {ImageOpNodeOperatorGradientRemoval} from "@app/textures/texture-editor/operator-stack/operators/highpass/node/node"
import {BrushSettings} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/brush-strokes/definition"
import {getOrComputeCachedUpstreamValue} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/cached-upstream-value"
import {computeSourceMean, SourceMean} from "@app/textures/texture-editor/operator-stack/operators/highpass/helpers/compute-source-mean"
import {BakedMaskCache} from "@app/textures/texture-editor/operator-stack/operators/shared/nodes/mask-brush-bake/baked-mask-cache"

export class OperatorHighpass extends OperatorBase<TextureEditNodes.OperatorHighpass> {
    readonly panelComponentType: OperatorPanelComponentType = HighpassPanelComponent
    readonly canvasToolbox: HighpassToolbox

    readonly type = "operator-highpass" as const

    constructor(callback: OperatorCallback, node: TextureEditNodes.OperatorHighpass | null) {
        super(
            callback,
            deepCopy(node) ?? {
                type: "operator-highpass",
                enabled: true,
                blurSettingsH: {
                    enabled: true,
                    smoothingDistance: 150,
                },
                blurSettingsV: {
                    enabled: true,
                    smoothingDistance: 150,
                },
                intensity: 1,
                wrapAround: true,
                angleOffset: 0,
                maskReference: undefined,
                correctionMode: "modulation",
            },
        )

        // convert legacy smoothingDistance to blurSettings
        if (this.node.smoothingDistance) {
            this.node.blurSettingsH = {
                enabled: true,
                smoothingDistance: this.node.smoothingDistance.x,
            }
            this.node.blurSettingsV = {
                enabled: true,
                smoothingDistance: this.node.smoothingDistance.y,
            }
            this.node.angleOffset = 0
            delete this.node.smoothingDistance
        }

        this.canvasToolbox = new HighpassToolbox(this)
        this._$brushSettings = signal<BrushSettings>(this.canvasToolbox.brushToolboxItem.brushSettings, {equal: () => false})
        this.$brushSettings = this._$brushSettings.asReadonly()
    }

    // OperatorBase
    // override async init() {
    //     await super.init()
    //     await this.updateDrawableImageRef()
    // }

    // OperatorBase
    override dispose(): void {
        this.sharedImageOpNodeBag.dispose()
        this.maskBakeCache.dispose()
        super.dispose()
        // this._maskDrawableImageHandle?.release()
        this.canvasToolbox.remove()
    }

    // OperatorBase
    async clone(): Promise<Operator> {
        const clonedOperator = new OperatorHighpass(this.callback, deepCopy(this.node))
        await clonedOperator.init()
        clonedOperator.canvasToolbox.brushToolboxItem.copyFrom(this.canvasToolbox.brushToolboxItem)
        return clonedOperator
    }

    private sharedImageOpNodeBag = new ImageOpNodeBag("OperatorHighpassShared")
    private maskBakeCache = new BakedMaskCache(this.callback.texturesApi, this.callback.organization.legacyId)

    // OperatorBase
    override invalidateUpstreamCaches(): void {
        this.node.sourceMeanCache = undefined
    }

    // OperatorBase
    protected override async createImageOpGraph(
        sourceImage: AnyImageOpResultNode,
        hints: OperatorProcessingHints,
        imageOpNodeBag: ImageOpNodeBag,
    ): Promise<AnyImageOpResultNode> {
        this.sharedImageOpNodeBag.beginFrame()
        let maskImage: ValueOrNode<AnyImageOpResult> | undefined = undefined
        this.canvasToolbox.brushToolboxItem.selected = this.node.maskReference !== undefined
        if (this.node.maskReference) {
            maskImage = await prepareAndBakeMinimalMask({
                sharedImageOpNodeBag: this.sharedImageOpNodeBag,
                sourceImage,
                mode: hints.mode,
                dataObjectId: this.node.maskReference.dataObjectId ? this.node.maskReference.dataObjectId : undefined,
                maskRegion: this.node.maskRegion,
                brushToolboxItem: this.canvasToolbox.brushToolboxItem,
                maskBakeCache: this.maskBakeCache,
                onBaked: (dataObjectId, region) => {
                    if (!this.node.maskReference) {
                        throw new Error("Mask reference is missing")
                    }
                    this.node.maskReference.dataObjectId = dataObjectId
                    this.node.maskRegion = region
                },
            })
        }
        let displayMode: z.infer<typeof DisplayMode>
        if (maskImage && hints.mode === "preview" && this.selected && this.$showMask()) {
            displayMode = "mask"
        } else if (hints.mode === "preview" && this.selected && this.$showLowpass()) {
            displayMode = "low-pass"
        } else {
            displayMode = "normal"
        }
        this.sharedImageOpNodeBag.endFrame()

        // sourceMean is only eagerly computed/cached for interactive preview, and only when it's
        // actually needed (displayMode === "normal") — for baking (final) it's left undefined and
        // ImageOpNodeOperatorGradientRemoval computes it live instead, folded into whatever imgproc
        // evaluation is already running rather than forcing a separate client-side download+evaluate
        // of every texture type's source image on every submit.
        let sourceMean: SourceMean | undefined
        if (hints.mode === "preview" && displayMode === "normal") {
            const {value, cache} = await getOrComputeCachedUpstreamValue(hints.textureType, this.node.sourceMeanCache, () =>
                computeSourceMean(this.callback.imageOpSystem, sourceImage),
            )
            sourceMean = value
            this.node.sourceMeanCache = cache
        }

        return imageOpNodeBag.getNode("gradientRemoval", ImageOpNodeOperatorGradientRemoval, {
            sourceImage,
            sourceMean,
            maskImage: maskImage,
            blurSettingsH: this.node.blurSettingsH,
            blurSettingsV: this.node.blurSettingsV,
            wrapAround: this.node.wrapAround,
            intensity: this.node.intensity,
            angleOffset: this.node.angleOffset,
            displayMode: displayMode,
            correctionMode: this.node.correctionMode,
        })
    }

    // OperatorBase
    // async queueImageOps(cmdQueue: ImageOpCommandQueue, input: OperatorInput, hints: OperatorProcessingHints): Promise<OperatorOutput> {
    //     cmdQueue.beginScope(this.type)
    //
    //     if (this._maskDrawableImageHandle) {
    //         this.canvasToolbox.brushToolboxItem.selected = true
    //         this.canvasToolbox.brushToolboxItem.flushBrushStrokesExt(cmdQueue, input.descriptor)
    //     } else {
    //         this.canvasToolbox.brushToolboxItem.selected = false
    //     }
    //
    //     const sourceImage = input
    //     const blurSettingsH = {
    //         enabled: this.node.blurSettingsH.enabled,
    //         smoothingDistance: Math.round(this.node.blurSettingsH.smoothingDistance),
    //     }
    //     const blurSettingsV = {
    //         enabled: this.node.blurSettingsV.enabled,
    //         smoothingDistance: Math.round(this.node.blurSettingsV.smoothingDistance),
    //     }
    //     const wrapAround = this.node.wrapAround
    //     const intensity = this.node.intensity ?? 1
    //     const useAngleOffset = (blurSettingsH.enabled || blurSettingsV.enabled) && blurSettingsH.smoothingDistance !== blurSettingsV.smoothingDistance
    //     const angleOffset = useAngleOffset ? this.node.angleOffset : 0
    //
    //     const applySourceImageConversion = (sourceImage: OperatorInput) => {
    //         const correctLuminanceOnly = false
    //         if (correctLuminanceOnly) {
    //             const grayscaleConversionMode = descriptorByTextureType(hints.textureType).isColorData ? "luminance" : "average"
    //             return toGrayscale(cmdQueue, {
    //                 sourceImage: sourceImage,
    //                 mode: grayscaleConversionMode,
    //             })
    //         } else {
    //             return sourceImage
    //         }
    //     }
    //     const filterSourceImage = applySourceImageConversion(sourceImage)
    //
    //     // blur to compute low-pass
    //     const rotationTransform = new Matrix3x2().rotate(angleOffset)
    //     const applyRotationAndBlur = (image: ImageRef, angle: number) => {
    //         let blurSource: ImageRef
    //         if (angle != 0) {
    //             const expandedSize = (() => {
    //                 const p00 = rotationTransform.multiplyVector(new Vector2(-image.descriptor.width, -image.descriptor.height).mulInPlace(0.5))
    //                 const p10 = rotationTransform.multiplyVector(new Vector2(image.descriptor.width, -image.descriptor.height).mulInPlace(0.5))
    //                 const p01 = rotationTransform.multiplyVector(new Vector2(-image.descriptor.width, image.descriptor.height).mulInPlace(0.5))
    //                 const p11 = rotationTransform.multiplyVector(new Vector2(image.descriptor.width, image.descriptor.height).mulInPlace(0.5))
    //                 const min = Vector2.min(p00, Vector2.min(p10, Vector2.min(p01, p11)))
    //                 const max = Vector2.max(p00, Vector2.max(p10, Vector2.max(p01, p11)))
    //                 return max.sub(min).add(new Vector2(blurSettingsH.smoothingDistance, blurSettingsV.smoothingDistance)).ceil()
    //             })()
    //             blurSource = affineTransform(cmdQueue, {
    //                 sourceImage: image,
    //                 transform: new Matrix3x2()
    //                     .translate(expandedSize.mul(0.5))
    //                     .rotate(angle)
    //                     .translate(new Vector2(image.descriptor.width, image.descriptor.height).mul(-0.5)),
    //                 addressMode: wrapAround ? "wrap" : "border",
    //                 resultImageOrDataType: createImage(cmdQueue, {
    //                     imageOrDescriptor: {
    //                         ...image.descriptor,
    //                         width: expandedSize.x,
    //                         height: expandedSize.y,
    //                     },
    //                     fillColor: undefined,
    //                 }),
    //             })
    //         } else {
    //             blurSource = image
    //         }
    //         return gaussianBlur(cmdQueue, {
    //             sourceImage: blurSource,
    //             sigma: {
    //                 x: (blurSettingsH.enabled ? blurSettingsH.smoothingDistance : sourceImage.descriptor.width) / 3,
    //                 y: (blurSettingsV.enabled ? blurSettingsV.smoothingDistance : sourceImage.descriptor.height) / 3,
    //             },
    //             borderMode: wrapAround ? "wrap" : angle != 0 ? "border" : "renormalize",
    //         })
    //     }
    //     let lowpassImage: OperatorInput = applyRotationAndBlur(filterSourceImage, -angleOffset)
    //     if (angleOffset != 0) {
    //         if (!wrapAround) {
    //             // manual renormalization for rotated border
    //             const imageMask = createImage(cmdQueue, {
    //                 imageOrDescriptor: {
    //                     width: filterSourceImage.descriptor.width,
    //                     height: filterSourceImage.descriptor.height,
    //                     channelLayout: "R",
    //                     dataType: "uint8norm",
    //                 },
    //                 fillColor: {r: 1},
    //             })
    //             const blurredRotatedImageMask = applyRotationAndBlur(imageMask, -angleOffset)
    //             lowpassImage = math(cmdQueue, {
    //                 operator: "/safe",
    //                 operandA: lowpassImage,
    //                 operandB: blurredRotatedImageMask,
    //             })
    //         }
    //         // rotate back
    //         lowpassImage = affineTransform(cmdQueue, {
    //             sourceImage: lowpassImage,
    //             transform: new Matrix3x2()
    //                 .translate(new Vector2(sourceImage.descriptor.width, sourceImage.descriptor.height).mul(0.5))
    //                 .rotate(angleOffset ?? 0)
    //                 .translate(Vector2.fromSize2Like(lowpassImage.descriptor).mul(-0.5)),
    //             resultImageOrDataType: createImage(cmdQueue, {
    //                 imageOrDescriptor: sourceImage.descriptor,
    //                 fillColor: undefined,
    //             }),
    //         })
    //     }
    //
    //     // get optional mask
    //     const mask = this._maskDrawableImageHandle
    //         ? drawableImage(cmdQueue, {
    //               drawableImageHandle: this._maskDrawableImageHandle.ref,
    //           })
    //         : undefined
    //     if (mask && cmdQueue.mode === "preview" && this.selected && this.showMask) {
    //         cmdQueue.endScope(this.type)
    //         return {
    //             resultImage: mask,
    //             options: {
    //                 stopEvaluation: true,
    //             },
    //         }
    //     } else if (cmdQueue.mode === "preview" && this.selected && this.showLowpass) {
    //         cmdQueue.endScope(this.type)
    //         return {
    //             resultImage: lowpassImage,
    //             options: {
    //                 stopEvaluation: true,
    //             },
    //         }
    //     } else {
    //         // compute mean
    //         const sourceImageMean = mean(cmdQueue, {
    //             sourceImage: filterSourceImage,
    //             resultDataType: "float16",
    //         }).mean
    //
    //         // compute highpass
    //         let highpassImage: OperatorInput
    //         if (this.correctionMode === "offset") {
    //             const meanMinusLowpass = math(cmdQueue, {
    //                 operator: "-",
    //                 operandA: sourceImageMean,
    //                 operandB: lowpassImage,
    //             })
    //             highpassImage = math(cmdQueue, {
    //                 operator: "+",
    //                 operandA: sourceImage,
    //                 operandB: meanMinusLowpass,
    //             })
    //         } else if (this.correctionMode === "modulation") {
    //             const meanDivLowpass = math(cmdQueue, {
    //                 operator: "/safe",
    //                 operandA: sourceImageMean,
    //                 operandB: lowpassImage,
    //             })
    //             highpassImage = math(cmdQueue, {
    //                 operator: "*",
    //                 operandA: sourceImage,
    //                 operandB: meanDivLowpass,
    //             })
    //         } else {
    //             assertNever(this.correctionMode)
    //         }
    //
    //         // optionally apply mask
    //         let resultImage: OperatorInput
    //         if (!mask) {
    //             resultImage = highpassImage
    //         } else {
    //             resultImage = blend(cmdQueue, {
    //                 backgroundImage: sourceImage,
    //                 foregroundImage: highpassImage,
    //                 alpha: mask,
    //                 blendMode: "normal",
    //                 premultipliedAlpha: false,
    //             })
    //         }
    //
    //         // optionally blend by intensity
    //         if (intensity !== 1) {
    //             resultImage = blend(cmdQueue, {
    //                 backgroundImage: sourceImage,
    //                 foregroundImage: resultImage,
    //                 alpha: intensity,
    //                 blendMode: "normal",
    //                 premultipliedAlpha: false,
    //             })
    //         }
    //
    //         // convert back to original data type
    //         resultImage = convert(cmdQueue, {
    //             sourceImage: resultImage,
    //             dataType: sourceImage.descriptor.dataType,
    //         })
    //
    //         cmdQueue.endScope(this.type)
    //         return {
    //             resultImage,
    //         }
    //     }
    // }

    private readonly _$showGuides = signal(false)
    readonly $showGuides = this._$showGuides.asReadonly()

    setShowGuides(value: boolean): void {
        this.canvasToolbox.showGuides = value
        this._$showGuides.set(value)
    }

    private readonly _$showLowpass = signal(false)
    readonly $showLowpass = this._$showLowpass.asReadonly()

    setShowLowpass(value: boolean): void {
        if (this.$showLowpass() === value) {
            return
        }
        this._$showLowpass.set(value)
        if (value) {
            this.setShowMask(false)
        }
        this.requestEval()
    }

    private readonly _$showMask = signal(false)
    readonly $showMask = this._$showMask.asReadonly()

    setShowMask(value: boolean): void {
        if (this.$showMask() === value) {
            return
        }
        this._$showMask.set(value)
        if (value) {
            this.setShowLowpass(false)
        }
        this.requestEval()
    }

    private readonly _$intensity = signal(this.node.intensity ?? 1)
    readonly $intensity = this._$intensity.asReadonly()

    setIntensity(value: number): void {
        if (this.node.intensity === value) {
            return
        }
        this.node.intensity = value
        this._$intensity.set(value)
        this.markEdited()
        this.requestEval()
    }

    private readonly _$useWrapAround = signal(this.node.wrapAround)
    readonly $useWrapAround = this._$useWrapAround.asReadonly()

    setUseWrapAround(value: boolean): void {
        if (this.node.wrapAround === value) {
            return
        }
        this.node.wrapAround = value
        this._$useWrapAround.set(value)
        this.markEdited()
        this.requestEval()
    }

    readonly $isIsotropic = computed(() => this.$useH() && this.$useV() && this.$smoothingDistanceH() === this.$smoothingDistanceV())

    private readonly _$useH = signal(this.node.blurSettingsH.enabled)
    readonly $useH = this._$useH.asReadonly()

    setUseH(value: boolean): void {
        if (this.node.blurSettingsH.enabled === value) {
            return
        }
        this.node.blurSettingsH.enabled = value
        this._$useH.set(value)
        this.markEdited()
        this.requestEval()
        this.canvasToolbox.updateLineGuides()
    }

    private readonly _$smoothingDistanceH = signal(this.node.blurSettingsH.smoothingDistance)
    readonly $smoothingDistanceH = this._$smoothingDistanceH.asReadonly()

    setSmoothingDistanceH(value: number): void {
        if (this.node.blurSettingsH.smoothingDistance === value) {
            return
        }
        this.node.blurSettingsH.smoothingDistance = value
        this._$smoothingDistanceH.set(value)
        this.markEdited()
        this.requestEval()
        this.canvasToolbox.updateLineGuides()
    }

    private readonly _$useV = signal(this.node.blurSettingsV.enabled)
    readonly $useV = this._$useV.asReadonly()

    setUseV(value: boolean): void {
        if (this.node.blurSettingsV.enabled === value) {
            return
        }
        this.node.blurSettingsV.enabled = value
        this._$useV.set(value)
        this.markEdited()
        this.requestEval()
        this.canvasToolbox.updateLineGuides()
    }

    private readonly _$smoothingDistanceV = signal(this.node.blurSettingsV.smoothingDistance)
    readonly $smoothingDistanceV = this._$smoothingDistanceV.asReadonly()

    setSmoothingDistanceV(value: number): void {
        if (this.node.blurSettingsV.smoothingDistance === value) {
            return
        }
        this.node.blurSettingsV.smoothingDistance = value
        this._$smoothingDistanceV.set(value)
        this.markEdited()
        this.requestEval()
        this.canvasToolbox.updateLineGuides()
    }

    private readonly _$angleOffset = signal(this.node.angleOffset)
    readonly $angleOffset = this._$angleOffset.asReadonly()

    setAngleOffset(value: number): void {
        if (this.node.angleOffset === value) {
            return
        }
        this.node.angleOffset = value
        this._$angleOffset.set(value)
        this.markEdited()
        this.requestEval()
        this.canvasToolbox.updateLineGuides()
    }

    private readonly _$hasMask = signal(this.node.maskReference !== undefined)
    readonly $hasMask = this._$hasMask.asReadonly()

    private readonly _$brushSettings!: WritableSignal<BrushSettings>
    readonly $brushSettings!: Signal<BrushSettings>

    setBrushSettings(value: BrushSettings): void {
        this._$brushSettings.set(value)
        this.canvasToolbox.brushToolboxItem.brushSettings = value
    }

    private readonly _$correctionMode = signal(this.node.correctionMode ?? "offset")
    readonly $correctionMode = this._$correctionMode.asReadonly()

    setCorrectionMode(value: TextureEditNodes.OperatorHighpassCorrectionMode): void {
        if (this.node.correctionMode === value) {
            return
        }
        this.node.correctionMode = value
        this._$correctionMode.set(value)
        this.markEdited()
        this.requestEval()
    }

    async addMask() {
        if (this.$hasMask()) {
            return
        }
        this.node.maskReference = {
            type: "data-object-reference",
            dataObjectId: "",
        }
        this._$hasMask.set(true)
        // await this.updateDrawableImageRef()
        this.markEdited()
        this.requestEval()
    }

    async removeMask() {
        if (!this.$hasMask()) {
            return
        }
        this.node.maskReference = undefined
        this.node.maskRegion = undefined
        this._$hasMask.set(false)
        this.setShowMask(false)
        // await this.updateDrawableImageRef()
        this.markEdited()
        this.requestEval()
    }

    // override async save(processingJobId: string) {
    //     if (this.node.maskReference) {
    //         if (!this._maskDrawableImageHandle) {
    //             throw new Error("Mask drawable image handle is missing")
    //         }
    //         this.node.maskReference.dataObjectId = await this.callback.drawableImageCache.getDataObjectId(this._maskDrawableImageHandle.ref.id)
    //     }
    //     return super.save(processingJobId)
    // }

    // private async updateDrawableImageRef() {
    //     this._maskDrawableImageHandle?.release()
    //     this._maskDrawableImageHandle = undefined
    //     this._maskDrawableImageHandle = this.node.maskReference
    //         ? await this.callback.drawableImageCache.createDrawableImage(
    //               this.node.maskReference.dataObjectId
    //                   ? {
    //                         dataObjectId: this.node.maskReference.dataObjectId,
    //                         channelLayout: "R",
    //                         dataType: "uint8norm",
    //                     }
    //                   : {
    //                         width: 1,
    //                         height: 1,
    //                         channelLayout: "R",
    //                         dataType: "uint8norm",
    //                     },
    //           )
    //         : undefined
    //     this.canvasToolbox.brushToolboxItem.setDrawableImage(this._maskDrawableImageHandle?.ref, this.node.maskReference?.dataObjectId ? "fill" : "reset")
    // }

    // private _brushSettings = new BrushSettings()
    // private _maskDrawableImageHandle: ManagedDrawableImageHandle | undefined
}

// type RotationAndBlurOperators = {
//     affineTransform?: ImageOpNodeAffineTransform
//     gaussianBlur?: ImageOpNodeGaussianBlur
// }
